"use client";

import React, { useState } from "react";
import { Check, ChevronDown, Loader, MapPin, MessageSquare, Phone, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RangeInput } from "@/components/ui/range-input";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CONTACT_INFO = {
  phone: "+852 2345 6789",
  email: "advisory@aoto-global.com",
  address: "Two IFC, 8 Finance Street, Central, Hong Kong"
};
const COUNTRY_OPTIONS = ["Saudi Arabia", "United Arab Emirates", "China", "Singapore", "Kazakhstan", "Indonesia", "Egypt", "Other"];
const PRODUCT_OPTIONS = ["Amusement Park Planning & Design", "Integrated EPCO Services", "Tourism Destination Operations", "Infrastructure Investment Analysis"];
function RfqForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [quantityDisplay, setQuantityDisplay] = useState(1000000);
  if (isSubmitted && !submitError) {
    return <div data-section-id="94536" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="h-16 w-16 bg-secondary/20 text-secondary rounded-full flex items-center justify-center mb-4">
          <Check className="h-8 w-8" />
        </div>
        <h3 className="text-2xl font-bold text-primary tracking-tight">Proposal Initiated</h3>
        <p className="text-slate-600 max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-white rounded-sm">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  const inputClasses = "flex h-10 w-full rounded-sm border-0 border-b border-slate-200 bg-transparent px-0 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-400 focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all";
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-slate-500">Principal Contact</FormLabel>
              <FormControl>
                <input type="text" placeholder="Full Name" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        required: "Organization name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-slate-500">Organization</FormLabel>
              <FormControl>
                <input type="text" placeholder="Company or Government Entity" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-slate-500">Corporate Email</FormLabel>
              <FormControl>
                <input type="email" placeholder="email@organization.com" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="country" rules={{
        required: "Please select a region"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs font-mono uppercase tracking-widest text-slate-500">Project Region</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full border-0 border-b border-slate-200 rounded-none px-0 focus:ring-0 focus:border-secondary shadow-none">
                    <SelectValue placeholder="Select target market" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="rounded-sm border-slate-200">
                  {COUNTRY_OPTIONS.map((country, i) => <SelectItem key={country} value={country} data-index={i}>
                      {country}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="product" rules={{
      required: "Please select a service category"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-mono uppercase tracking-widest text-slate-500">Service Requirement</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="w-full border-0 border-b border-slate-200 rounded-none px-0 focus:ring-0 focus:border-secondary shadow-none">
                  <SelectValue placeholder="Select EPCO Service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-sm border-slate-200">
                {PRODUCT_OPTIONS.map((product, i) => <SelectItem key={product} value={product} data-index={i}>
                    {product}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="quantity" rules={{
      required: "Budget range is required",
      min: {
        value: 100000,
        message: "Minimum scope starts at $100k"
      }
    }} render={({
      field
    }) => <FormItem>
            <div className="flex justify-between items-center mb-2">
              <FormLabel className="mb-0 text-xs font-mono uppercase tracking-widest text-slate-500">Estimated Project Scale (USD)</FormLabel>
              <span className="text-sm font-bold text-secondary bg-secondary/5 px-2 py-1 rounded-sm">
                ${quantityDisplay.toLocaleString()}
              </span>
            </div>
            <FormControl>
              <div className="pt-2 pb-4">
                <RangeInput min={100000} max={50000000} step={500000} value={field.value || 1000000} onValueChange={val => {
            field.onChange(val);
            setQuantityDisplay(val);
          }} className="w-full accent-secondary" />
              </div>
            </FormControl>
            <FormDescription className="text-[10px] text-slate-400">Indicate approximate project valuation for feasibility matching.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-xs font-mono uppercase tracking-widest text-slate-500">Project Brief</FormLabel>
            <FormControl>
              <textarea placeholder="Briefly describe the vision, site context, and timeline..." className={`min-h-[80px] resize-y ${inputClasses}`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="whatsapp_opt_in" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-sm border border-slate-100 p-4 bg-slate-50/50">
            <FormControl>
              <input type="checkbox" className="h-4 w-4 mt-0.5 rounded-none border-slate-300 text-primary focus:ring-primary accent-primary" checked={field.value} onChange={field.onChange} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer text-sm font-medium text-primary">Priority Advisory Channel</FormLabel>
              <FormDescription className="text-xs text-slate-500">
                Enable secure direct messaging for rapid executive coordination.
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/10 rounded-sm text-destructive text-xs font-medium">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-sm font-bold uppercase tracking-widest bg-primary hover:bg-primary/95 text-white rounded-sm transition-all group">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Analyzing Parameters...
          </> : <>
            <Send className="mr-2 h-4 w-4 text-secondary group-hover:translate-x-1 transition-transform" />
            Submit Project Proposal
          </>}
      </Button>
    </Form>;
}
export default function StrategicRfqGateway() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="connect-with-our-experts" className="relative bg-background py-20 lg:py-32 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0 opacity-20">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-secondary/10 blur-[120px]" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-primary/10 blur-[100px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-32 space-y-8 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center rounded-none border-l-2 border-secondary bg-primary/5 px-4 py-1.5 text-xs font-mono uppercase tracking-widest text-primary">
                <span className="flex h-1.5 w-1.5 rounded-full bg-secondary mr-3 animate-pulse"></span>
                Strategic Development Partner
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-primary leading-[1.1]">
                From Vision to <span className="text-secondary italic">Destination.</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed font-light">
                Partner with AOTO for world-class tourism development. Our integrated EPCO framework ensures seamless transition from conceptual vision to operational excellence across global emerging markets.
              </p>
            </div>

            <div className="space-y-6 pt-8 border-t border-slate-200">
              <h3 className="text-sm font-mono uppercase tracking-widest text-primary font-bold">Global Advisory Desk</h3>
              
              <div className="space-y-5">
                <div className="flex items-start space-x-4">
                  <div className="mt-1 bg-primary text-secondary p-2.5 rounded-sm">
                    <Phone className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Project Advisory</p>
                    <p className="text-lg font-bold text-primary tracking-tight">+00 852 7019 1618</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="mt-1 bg-primary text-secondary p-2.5 rounded-sm">
                    <MessageSquare className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Direct Inquiries</p>
                    <p className="text-lg font-bold text-primary tracking-tight">aotonexus26@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="mt-1 bg-primary text-secondary p-2.5 rounded-sm">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Global Headquarters</p>
                    <p className="text-lg font-bold text-primary tracking-tight leading-snug">RM05,9/F, THE RAINBOW, NO.22, WANG YIP ST SOUTH, YUEN LONG HONG KONG</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Card className="border-0 shadow-2xl shadow-primary/5 bg-white/80 backdrop-blur-md rounded-sm">
              <CardHeader className="pb-8 border-b border-slate-100 bg-slate-50/50 rounded-t-sm">
                <CardTitle className="text-2xl font-bold tracking-tight text-primary">Project Inquiry Gateway</CardTitle>
                <CardDescription className="text-slate-500 font-light text-base">
                  Submit your high-level parameters to initiate an integrated EPCO feasibility review.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-10 px-8 pb-10">
                <FormProvider formId="rfq_gateway_home" sectionName="Strategic RFQ Gateway" defaultValues={{
                name: "",
                company: "",
                country: "",
                email: "",
                product: "",
                quantity: 1000000,
                message: "",
                whatsapp_opt_in: false
              }} submitText="Submit Project Proposal" successMessage="Your inquiry has been received by our executive board. A senior advisor will contact you within 24 hours to schedule a preliminary consultation.">
                  <RfqForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>

      {}
      <Link to={`https://wa.me/${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} newTab className="fixed bottom-8 right-8 z-50 flex items-center justify-center h-14 w-14 bg-primary text-secondary rounded-sm shadow-xl hover:scale-105 hover:bg-primary/95 transition-all duration-300 group" aria-label="Contact advisory on WhatsApp">
        <MessageSquare className="h-6 w-6" />
        <span className="absolute right-16 bg-white border border-slate-100 text-primary text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
          Priority Advisory
        </span>
      </Link>
    </section>;
}